/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';

import { DeviceFeatures } from '../../../../core/device';
import { BrandingUiInfoKeys } from '../../../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../../../core/interactors/ServicesClientImpl';
import { Locale } from '../../../../core/locale/Locale';
import { LocaleKeys } from '../../../../core/locale/LocaleKeys';
import { PlaybackContentType } from '../../../../core/playback/enums';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { VideoPlaybackController } from '../../../../core/videoplayer/VideoPlaybackController';
import {
    setActivePositionsAction,
    setCurrentActiveIndexAction,
} from '../../../../store/videoplayerui/infobanner/infobanner.actions';
import { MediaButtonTypes } from '../../../../store/videoplayerui/infobanner/mediabuttontypes';
import { MediaButton } from '../MediaButton';
import { MediaButtonClasses } from '../MediaButton/MediaButtonClasses';
import { MediaButtonProps } from './MediaButtonProps';

import './MediaButtonsPanel.scss';

const MediaButtonsPanel: FunctionComponent<MediaButtonProps> = (props): JSX.Element => {
    const {
        isAvailableInSocu,
        isStartFromBufferButtonDisabled,
        isReplayButtonDisabled,
        isRewindButtonDisabled,
        isPlayPauseButtonDisabled,
        isForwardButtonDisabled,
        isAdvanceButtonDisabled,
        isGoToLiveButtonDisabled,
        isLiveButtonDisabled,
    } = props;

    const dispatch = useTivoDispatch();

    const isPlaying = useTivoSelector((state: RootState) => state.videoPlayer.isPlaying);
    const { currentActiveIndex } = useTivoSelector((state: RootState) => state.infoBanner);
    const isTrickplayMenuActive = useTivoSelector(
        (state: RootState) => state.infoBanner.isTrickplayMenuActive,
    );
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    const isPlaybackStarted = oVideoPlaybackController.isPlaybackStarted();
    const isPlayerLoaded = oVideoPlaybackController.isPlayerLoaded();

    const services = new ServicesClientImpl();

    const isSocuEnabled = DeviceFeatures.isSocuEnabled();

    const updatePosition = (type: number, isActive: boolean, isFocus: boolean) => ({
        type,
        isActive,
        isFocus,
    });

    const availableActivePositions: any = useMemo(
        () =>
            new Map([
                [
                    MediaButtonTypes.START_FROM_BUFFER_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.START_FROM_BUFFER_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.REPLAY_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.REPLAY_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.REWIND_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.REWIND_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.FORWARD_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.FORWARD_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.ADVANCE_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.ADVANCE_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.GO_TO_LIVETV_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.GO_TO_LIVETV_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.LIVE_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.LIVE_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.INFO_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.INFO_BUTTON_INDEX, true, false),
                ],
                [
                    MediaButtonTypes.OLG_BUTTON_INDEX,
                    updatePosition(MediaButtonTypes.OLG_BUTTON_INDEX, true, false),
                ],
            ]),
        [currentActiveIndex, isPlaybackStarted, isPlayerLoaded],
    );

    useEffect(() => {
        if (isStartFromBufferButtonDisabled)
            availableActivePositions.set(
                MediaButtonTypes.START_FROM_BUFFER_BUTTON_INDEX,
                updatePosition(MediaButtonTypes.START_FROM_BUFFER_BUTTON_INDEX, false, false),
            );
        if (isReplayButtonDisabled)
            availableActivePositions.set(
                MediaButtonTypes.REPLAY_BUTTON_INDEX,
                updatePosition(MediaButtonTypes.REPLAY_BUTTON_INDEX, false, false),
            );
        if (isRewindButtonDisabled)
            availableActivePositions.set(
                MediaButtonTypes.REWIND_BUTTON_INDEX,
                updatePosition(MediaButtonTypes.REWIND_BUTTON_INDEX, false, false),
            );
        if (isForwardButtonDisabled)
            availableActivePositions.set(
                MediaButtonTypes.FORWARD_BUTTON_INDEX,
                updatePosition(MediaButtonTypes.FORWARD_BUTTON_INDEX, false, false),
            );
        if (isAdvanceButtonDisabled)
            availableActivePositions.set(
                MediaButtonTypes.ADVANCE_BUTTON_INDEX,
                updatePosition(MediaButtonTypes.ADVANCE_BUTTON_INDEX, false, false),
            );
        if (isGoToLiveButtonDisabled)
            availableActivePositions.set(
                MediaButtonTypes.GO_TO_LIVETV_BUTTON_INDEX,
                updatePosition(MediaButtonTypes.GO_TO_LIVETV_BUTTON_INDEX, false, false),
            );

        if (isLiveButtonDisabled) {
            availableActivePositions.set(
                MediaButtonTypes.LIVE_BUTTON_INDEX,
                updatePosition(MediaButtonTypes.LIVE_BUTTON_INDEX, false, false),
            );
        }

        dispatch(setActivePositionsAction(availableActivePositions));
        if (availableActivePositions.get(currentActiveIndex)?.isActive) {
            dispatch(setCurrentActiveIndexAction(currentActiveIndex));
            if (currentActiveIndex !== MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX) {
                availableActivePositions.set(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX, {
                    ...availableActivePositions.get(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX),
                    isFocus: false,
                });
            }
            availableActivePositions.set(currentActiveIndex, {
                ...availableActivePositions.get(currentActiveIndex),
                isFocus: true,
            });
        } else {
            dispatch(setCurrentActiveIndexAction(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX));
            availableActivePositions.set(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX, {
                ...availableActivePositions.get(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX),
                isFocus: true,
            });
        }
    }, [
        isStartFromBufferButtonDisabled,
        isReplayButtonDisabled,
        isRewindButtonDisabled,
        isPlayPauseButtonDisabled,
        isForwardButtonDisabled,
        isAdvanceButtonDisabled,
        isGoToLiveButtonDisabled,
        isLiveButtonDisabled,
        isTrickplayMenuActive,
        availableActivePositions,
        currentActiveIndex,
    ]);

    const getStartFromBufferAndStartOverText = (): string => {
        // If Socu content is already playing, show Start over with small 'o'
        if (
            oVideoPlaybackController.getPlayable()?.playbackContentType !== PlaybackContentType.LIVE
        )
            return Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_START_OVER);

        // if socu is available then show Start Over
        if (isSocuEnabled && isAvailableInSocu) {
            return services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER);
        }

        return Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_START_FROM_BUFFER);
    };

    const getGoToLiveAndGoToEndText = (): string => {
        // If Socu content is already playing, show Go to End
        if (
            oVideoPlaybackController.getPlayable()?.playbackContentType !== PlaybackContentType.LIVE
        ) {
            return Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_GO_TO_END);
        }
        return Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_GO_TO_LIVE);
    };

    const mediaButtons = () => {
        const list: JSX.Element[] = [];
        availableActivePositions.forEach((_value, key) => {
            let tHighlighted: boolean | undefined = false;
            let tDisabled: boolean | undefined = false;
            let tType = '';
            let buttonString = '';
            let tLabelPostion = '';

            switch (key) {
                case MediaButtonTypes.START_FROM_BUFFER_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.START_FROM_BUFFER_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = isStartFromBufferButtonDisabled;
                    tType = MediaButtonClasses.START_FROM_BUFFER;
                    buttonString = getStartFromBufferAndStartOverText();
                    break;
                case MediaButtonTypes.REPLAY_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.REPLAY_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = isReplayButtonDisabled;
                    tType = MediaButtonClasses.REPLAY;
                    buttonString = Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_REPLAY);
                    break;
                case MediaButtonTypes.REWIND_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.REWIND_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = isRewindButtonDisabled;
                    tType = MediaButtonClasses.REWIND;
                    buttonString = Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_REWIND);
                    break;
                case MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = isPlayPauseButtonDisabled;
                    tType = isPlaying ? MediaButtonClasses.PAUSE : MediaButtonClasses.PLAY;
                    buttonString = isPlaying
                        ? Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_PAUSE)
                        : Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_PLAY);
                    break;
                case MediaButtonTypes.FORWARD_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.FORWARD_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = isForwardButtonDisabled;
                    tType = MediaButtonClasses.FORWARD;
                    buttonString = Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_FORWARD);
                    break;
                case MediaButtonTypes.ADVANCE_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.ADVANCE_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = isAdvanceButtonDisabled;
                    tType = MediaButtonClasses.ADVANCE;
                    buttonString = Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_ADVANCE);
                    break;
                case MediaButtonTypes.GO_TO_LIVETV_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.GO_TO_LIVETV_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = isGoToLiveButtonDisabled;
                    tType = MediaButtonClasses.GO_TO_LIVE;
                    buttonString = getGoToLiveAndGoToEndText();
                    break;
                case MediaButtonTypes.LIVE_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.LIVE_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = isLiveButtonDisabled;
                    tType = MediaButtonClasses.LIVE;
                    buttonString = Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_LIVE);
                    break;
                case MediaButtonTypes.INFO_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.INFO_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = false;
                    tType = MediaButtonClasses.INFO;
                    buttonString = 'Info';
                    tLabelPostion = 'info-btn';
                    break;
                case MediaButtonTypes.OLG_BUTTON_INDEX:
                    tHighlighted = availableActivePositions.get(
                        MediaButtonTypes.OLG_BUTTON_INDEX,
                    )?.isFocus;
                    tDisabled = false;
                    tType = MediaButtonClasses.OLG;
                    buttonString = Locale.getString(LocaleKeys.LIVE_TV_MEDIA_BUTTON_OLG);
                    tLabelPostion = 'one-line-guide-btn';
                    break;
                default:
            }
            list.push(
                <MediaButton
                    key={`media-button-${key}`}
                    highlighted={tHighlighted}
                    disabled={tDisabled}
                    type={tType}
                    labelPostion={tLabelPostion}
                    mediaButtonIndex={key}
                >
                    {buttonString}
                </MediaButton>,
            );
        });
        return list;
    };

    return <div className="media-buttons-container">{mediaButtons()}</div>;
};

MediaButtonsPanel.propTypes = {
    isAvailableInSocu: PropTypes.bool,
    isStartFromBufferButtonDisabled: PropTypes.bool,
    isReplayButtonDisabled: PropTypes.bool,
    isRewindButtonDisabled: PropTypes.bool,
    isPlayPauseButtonDisabled: PropTypes.bool,
    isForwardButtonDisabled: PropTypes.bool,
    isAdvanceButtonDisabled: PropTypes.bool,
    isGoToLiveButtonDisabled: PropTypes.bool,
    isLiveButtonDisabled: PropTypes.bool,
};

export default MediaButtonsPanel;
